import { usePostHog } from 'posthog-js/react';
import { type RefObject, useEffect, useRef } from 'react';

import { EVENTS } from '@apps/web/lib/events';

const LONG_FRAME_THRESHOLD_MS = 50;

export type V3ChatPerformanceAttempt = {
    readonly id: number;
    readonly startedAtMs: number;
};

type ActiveTurn = {
    readonly attempt: V3ChatPerformanceAttempt;
    readonly baselineAssistantRevisions: ReadonlySet<string>;
    readonly firstActivityAtMs: number | null;
    readonly firstTextAtMs: number | null;
    readonly longFrameCount: number;
    readonly maxFrameGapMs: number;
    readonly liveStreamErrorCount: number;
};

function durationMs(startedAtMs: number, completedAtMs: number): number {
    return Math.max(0, Math.round(completedAtMs - startedAtMs));
}

export function useV3ChatPerformance({
    assistantRevisions,
    attempt,
    composerReady,
    composerContainerRef,
    composerReadySentRef,
    isBusy,
    liveStreamErrorCount,
    mode,
    onAttemptCompleted,
    organizationId,
    sourceVersion,
    surface,
    surfaceStartedAtMs,
}: {
    readonly assistantRevisions: ReadonlyArray<string>;
    readonly attempt: V3ChatPerformanceAttempt | null;
    readonly composerReady: boolean;
    readonly composerContainerRef: RefObject<HTMLDivElement | null>;
    readonly composerReadySentRef: RefObject<boolean>;
    readonly isBusy: boolean;
    readonly liveStreamErrorCount: number;
    readonly mode: string;
    readonly onAttemptCompleted: (attemptId: number) => void;
    readonly organizationId: string | null;
    readonly sourceVersion: 'v2' | 'v3';
    readonly surface: 'extension' | 'web';
    readonly surfaceStartedAtMs: number;
}) {
    const posthog = usePostHog();
    const activeTurnRef = useRef<ActiveTurn | null>(null);

    useEffect(() => {
        if (composerReadySentRef.current || !composerReady) {
            return;
        }
        const frameId = window.requestAnimationFrame(() => {
            const editor = composerContainerRef.current?.querySelector(
                '[role="textbox"][contenteditable="true"]',
            );
            if (editor === null || editor === undefined) {
                return;
            }
            composerReadySentRef.current = true;
            posthog?.capture(EVENTS.V3_CHAT_COMPOSER_READY, {
                duration_ms: durationMs(surfaceStartedAtMs, performance.now()),
                mode,
                organization_id: organizationId,
                source_version: sourceVersion,
                surface,
            });
        });
        return () => window.cancelAnimationFrame(frameId);
    }, [
        composerContainerRef,
        composerReady,
        composerReadySentRef,
        mode,
        organizationId,
        posthog,
        sourceVersion,
        surface,
        surfaceStartedAtMs,
    ]);

    useEffect(() => {
        if (attempt === null) {
            activeTurnRef.current = null;
            return;
        }
        if (activeTurnRef.current?.attempt.id === attempt.id) {
            return;
        }
        activeTurnRef.current = {
            attempt,
            baselineAssistantRevisions: new Set(assistantRevisions),
            firstActivityAtMs: null,
            firstTextAtMs: null,
            longFrameCount: 0,
            maxFrameGapMs: 0,
            liveStreamErrorCount: 0,
        };
    }, [assistantRevisions, attempt]);

    useEffect(() => {
        const activeTurn = activeTurnRef.current;
        if (activeTurn === null || activeTurn.firstTextAtMs !== null) {
            return;
        }
        const hasNewAssistantText = assistantRevisions.some(
            (revision) => !activeTurn.baselineAssistantRevisions.has(revision),
        );
        if (hasNewAssistantText) {
            activeTurnRef.current = {
                ...activeTurn,
                firstTextAtMs: performance.now(),
            };
        }
    }, [assistantRevisions]);

    useEffect(() => {
        const activeTurn = activeTurnRef.current;
        if (activeTurn !== null) {
            activeTurnRef.current = {
                ...activeTurn,
                liveStreamErrorCount: Math.max(
                    activeTurn.liveStreamErrorCount,
                    liveStreamErrorCount,
                ),
            };
        }
    }, [liveStreamErrorCount]);

    useEffect(() => {
        const activeTurn = activeTurnRef.current;
        if (activeTurn === null) {
            return;
        }
        if (isBusy && activeTurn.firstActivityAtMs === null) {
            const frameId = window.requestAnimationFrame(() => {
                const currentTurn = activeTurnRef.current;
                if (currentTurn?.attempt.id === activeTurn.attempt.id) {
                    activeTurnRef.current = {
                        ...currentTurn,
                        firstActivityAtMs: performance.now(),
                    };
                }
            });
            return () => window.cancelAnimationFrame(frameId);
        }
        if (!isBusy && activeTurn.firstActivityAtMs !== null) {
            const completedAtMs = performance.now();
            posthog?.capture(EVENTS.V3_CHAT_TURN_PERFORMANCE, {
                first_activity_ms: durationMs(
                    activeTurn.attempt.startedAtMs,
                    activeTurn.firstActivityAtMs,
                ),
                first_text_ms:
                    activeTurn.firstTextAtMs === null
                        ? null
                        : durationMs(
                              activeTurn.attempt.startedAtMs,
                              activeTurn.firstTextAtMs,
                          ),
                live_stream_error_count: activeTurn.liveStreamErrorCount,
                long_frame_count: activeTurn.longFrameCount,
                max_frame_gap_ms: Math.round(activeTurn.maxFrameGapMs),
                mode,
                organization_id: organizationId,
                source_version: sourceVersion,
                stream_duration_ms: durationMs(
                    activeTurn.firstActivityAtMs,
                    completedAtMs,
                ),
                surface,
                total_duration_ms: durationMs(
                    activeTurn.attempt.startedAtMs,
                    completedAtMs,
                ),
            });
            activeTurnRef.current = null;
            onAttemptCompleted(activeTurn.attempt.id);
        }
        return undefined;
    }, [
        isBusy,
        mode,
        onAttemptCompleted,
        organizationId,
        posthog,
        sourceVersion,
        surface,
    ]);

    useEffect(() => {
        if (!isBusy || activeTurnRef.current === null) {
            return;
        }
        let frameId: number | null = null;
        let lastFrameAtMs: number | null = null;
        const measureFrame = (frameAtMs: number) => {
            const activeTurn = activeTurnRef.current;
            if (activeTurn === null) {
                return;
            }
            if (
                document.visibilityState === 'visible' &&
                lastFrameAtMs !== null
            ) {
                const frameGapMs = frameAtMs - lastFrameAtMs;
                if (frameGapMs >= LONG_FRAME_THRESHOLD_MS) {
                    activeTurnRef.current = {
                        ...activeTurn,
                        longFrameCount: activeTurn.longFrameCount + 1,
                        maxFrameGapMs: Math.max(
                            activeTurn.maxFrameGapMs,
                            frameGapMs,
                        ),
                    };
                }
            }
            lastFrameAtMs =
                document.visibilityState === 'visible' ? frameAtMs : null;
            frameId = window.requestAnimationFrame(measureFrame);
        };
        frameId = window.requestAnimationFrame(measureFrame);
        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [isBusy]);
}
